import { HttpResponse, HttpResponseBase } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { IHeadersMap } from './filesList.service.model';

class ApiException extends Error {
  constructor(
    public message: string,
    public status: number,
    public response: string,
    public headers: IHeadersMap,
    public result: any
  ) {
    super();
  }
}
  
export function throwException(message: string, status: number, response: string, headers: IHeadersMap, result?: any): Observable<never> {
  const exception = result || new ApiException(message, status, response, headers, null);
  return throwError(exception);
}

export function getResponseBlob(response: HttpResponse<Blob>): Blob {
  let blob: Blob;
  if (response instanceof HttpResponse) {
    blob = response.body;
  }
  if (!blob && response['error'] instanceof Blob) {
    blob = response['error'];
  }
  return blob;
}

export function getResponseHeaders(response: HttpResponse<Blob>): IHeadersMap {
  const headers: IHeadersMap = {};
  if (response.headers) { 
    for (let key of response.headers.keys()) { 
      headers[key] = response.headers.get(key);
    }
  };
  return headers;
}

export function blobToText(blob: Blob): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next('');
      observer.complete();
    } else {
      let reader = new FileReader();
      reader.onload = event => {
        observer.next(event.target['result']);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}

export function getResponseText(response: HttpResponse<Blob>): Observable<string> {
  const blob = getResponseBlob(response);
  return blobToText(blob);
}

const FAILED_TO_PARSE_BLOB_TEXT = 'Could not parse FileList endpoint response text.';

export function customErrorMessage(response: HttpResponse<Blob>, message: string, parseResult = false): Observable<never> {
  const headers = getResponseHeaders(response);
  return getResponseText(response).pipe(mergeMap(text => {
    if (parseResult) {
      let result = null;
      try {
        result = !text ? null : JSON.parse(text);
      } catch (error) {
        console.error(FAILED_TO_PARSE_BLOB_TEXT);
      }
      return throwException(message, response.status, text, headers, result);
    } else {
      return throwException(message, response.status, text, headers);
    }
  }));
}

export function parseSuccessDetails<TParsedResponse = any>(response: HttpResponse<Blob>): Observable<TParsedResponse> {
  return getResponseText(response).pipe(mergeMap(text => {
    let result: TParsedResponse = null;
    try {
      result = !text ? null : JSON.parse(text);
    } catch (error) {
      console.error(FAILED_TO_PARSE_BLOB_TEXT);
    }
    return of(result);
  }));
}

export function isNotSuccessCode(statusCode: number) {
  return statusCode !== 200 && statusCode !== 204;
}

export function tryAddingMoreContextToErrorMessage(responseHandler: (errorResponse: unknown) => Observable<any>) {
  return function (errorResponse: unknown) {
    if (errorResponse instanceof HttpResponseBase) {
      try {
        return responseHandler(errorResponse);
      } catch (e) {
        return throwError(e);
      }
    }
    return throwError(errorResponse);
  };
}

export function appendQueryParamSymbolToUrlString(url: string): string {
  const noTrailingQueryParamSymbol = /[?&]$/.test(url) === false;
  const containsQueryParamInit = url.includes('?');
  return noTrailingQueryParamSymbol ? `${url}${containsQueryParamInit ? '&' : '?'}` : url;
}

export function appendQueryParamToUrlString(url: string, name: string, value: unknown): string {
  if (value === undefined) return url;
  return `${appendQueryParamSymbolToUrlString(url)}${name}=${encodeURIComponent(`${value}`)}`;
}